import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo, { localBusinessSchema, faqSchema } from "@/components/Seo";
import Reveal from "@/components/Reveal";
import Faq from "@/components/Faq";
import CtaBand from "@/components/CtaBand";
import { LatestJournal } from "@/components/LatestJournal";
import { SITE, SERVICES, HOME_FAQS, HERO_IMAGE, CLINIC_IMAGE, THERAPIST_IMAGE } from "@/data/site";

const APPROACH = [
  { n: "I", t: "Begin with stillness", d: "Therapy starts with creating space — the breath of room you need to actually hear yourself again." },
  { n: "II", t: "Evidence, gently held", d: "CBT, ACT, IFS and mindfulness — the modern science of healing, delivered with old-soul care." },
  { n: "III", t: "Move at your pace", d: "No prescribed timeline, no performance. Progress that respects your nervous system." },
];

const LETTERS = [
  { q: "I finally feel like I have a place to put it all down.", a: "M., 32" },
  { q: "It's the first room I've ever cried in without apologising.", a: "J., 41" },
  { q: "They didn't fix me. They reminded me I wasn't broken.", a: "A., 36" },
  { q: "The most thoughtful, unhurried care I have ever received.", a: "R., 49" },
];

export default function Home() {
  return (
    <>
      <Seo
        title="Let Go Wellness — Therapy in Waterbury, CT"
        description="Compassionate mental health therapy in Waterbury, CT. Anxiety, depression, couples & family, burnout and life transitions. In-person & telehealth across Connecticut."
        path="/"
        image={HERO_IMAGE}
        jsonLd={[localBusinessSchema, faqSchema(HOME_FAQS)]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-6 py-20 md:grid-cols-2 md:py-28">
          <Reveal>
            <div className="overline mb-6">Mental Health Therapy · Waterbury, CT</div>
            <h1 className="font-serif text-5xl leading-[1.05] text-ink sm:text-6xl lg:text-7xl">
              A quiet space to <span className="italic text-clay">let go</span>, and gently return to yourself.
            </h1>
            <p className="mt-8 max-w-md font-sans text-base leading-relaxed text-ink-soft">
              Evidence-based care for anxiety, depression, relationships, burnout and the in-between seasons of life — in person at our Waterbury clinic, or by secure telehealth across Connecticut.
            </p>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <Link to="/new-patient" data-testid="hero-enroll" className="rounded-full bg-ink px-8 py-3.5 text-center font-sans text-sm text-linen transition-colors hover:bg-ink/85">
                Enroll as a new patient
              </Link>
              <Link to="/contact" data-testid="hero-visit" className="rounded-full border border-border px-8 py-3.5 text-center font-sans text-sm text-ink-soft transition-colors hover:bg-white">
                Visit the clinic
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src={HERO_IMAGE} alt="Calm, sunlit therapy room at Let Go Wellness in Waterbury, CT" className="h-full w-full object-cover" loading="eager" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Services */}
      <section className="border-b border-border py-24">
        <div className="mx-auto max-w-6xl px-6">
          <div className="overline mb-4">01 — What we treat</div>
          <h2 className="max-w-2xl font-serif text-4xl text-ink sm:text-5xl">Care for the <span className="italic text-clay">whole</span> of you.</h2>
          <p className="mt-6 max-w-xl font-sans text-base leading-relaxed text-ink-soft">From everyday anxiety to life's hardest transitions, we weave evidence-based modalities with deep presence — met with steadiness, warmth and zero judgment.</p>
          <div className="mt-14 grid gap-px overflow-hidden border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`home-service-${s.slug}`}
                className="group flex flex-col justify-between bg-white p-8 transition-colors hover:bg-linen">
                <div>
                  <div className="overline mb-4">{s.num}</div>
                  <h3 className="font-serif text-2xl text-ink">{s.short}</h3>
                  <p className="mt-3 font-sans text-sm leading-relaxed text-ink-soft">{s.tagline}</p>
                </div>
                <span className="mt-8 inline-flex items-center gap-2 font-sans text-xs text-clay transition-transform group-hover:translate-x-1">Learn more <ArrowRight size={13} /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="border-b border-border bg-white py-24">
        <div className="mx-auto grid max-w-6xl items-center gap-14 px-6 md:grid-cols-2">
          <Reveal>
            <div className="aspect-[4/5] overflow-hidden">
              <img src={CLINIC_IMAGE} alt="Warm, linen-toned clinic interior" className="h-full w-full object-cover" loading="lazy" />
            </div>
          </Reveal>
          <div>
            <div className="overline mb-4">02 — Our approach</div>
            <h2 className="font-serif text-4xl text-ink sm:text-5xl">What if healing didn't feel like <span className="italic text-clay">work?</span></h2>
            <p className="mt-6 font-sans text-base leading-relaxed text-ink-soft">People heal in rooms where they feel deeply safe. Everything we've built — from the slow morning light to the way we practice — is in service of that one feeling.</p>
            <div className="mt-10 space-y-8">
              {APPROACH.map((a) => (
                <div key={a.n} className="flex gap-6">
                  <div className="font-serif text-2xl italic text-clay">{a.n}</div>
                  <div>
                    <h3 className="font-serif text-xl text-ink">{a.t}</h3>
                    <p className="mt-1 font-sans text-sm leading-relaxed text-ink-soft">{a.d}</p>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/about" data-testid="home-about-link" className="mt-10 inline-flex items-center gap-2 font-sans text-sm text-ink underline-offset-4 hover:underline">Read our full approach <ArrowRight size={14} /></Link>
          </div>
        </div>
      </section>

      {/* Meet Waleska */}
      <section className="border-b border-border bg-linen py-20">
        <div className="mx-auto grid max-w-5xl items-center gap-10 px-6 md:grid-cols-[280px_1fr]">
          <Reveal>
            <div className="mx-auto aspect-[4/5] w-full max-w-[280px] overflow-hidden">
              <img src={THERAPIST_IMAGE} alt="Waleska Castillo, LPC, therapist in Waterbury, CT" className="h-full w-full object-cover object-top" loading="lazy" />
            </div>
          </Reveal>
          <div>
            <div className="overline mb-4">Your therapist</div>
            <h2 className="font-serif text-4xl text-ink sm:text-5xl">Meet <span className="italic text-clay">Waleska.</span></h2>
            <p className="mt-3 font-sans text-xs uppercase tracking-[0.18em] text-clay">LPC · CT License #003511 · 15 years of experience</p>
            <p className="mt-5 max-w-lg font-sans text-base leading-relaxed text-ink-soft">
              A Licensed Professional Counselor with over 15 years of experience, fully bilingual in English and Spanish. Warm, grounded and unhurried — she meets the whole person, not just the symptom.
            </p>
            <Link to="/therapists/waleska-castillo" data-testid="home-meet-waleska" className="mt-8 inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3 font-sans text-sm text-linen transition-colors hover:bg-ink/85">
              Meet Waleska <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="border-b border-border py-24">
        <div className="mx-auto max-w-6xl px-6">
          <div className="overline mb-4">03 — In their words</div>
          <h2 className="font-serif text-4xl text-ink sm:text-5xl">Real letters, <span className="italic text-clay">real people.</span></h2>
          <div className="mt-14 grid gap-px overflow-hidden border border-border bg-border md:grid-cols-2">
            {LETTERS.map((l, i) => (
              <figure key={i} className="bg-white p-10">
                <blockquote className="font-serif text-2xl italic leading-relaxed text-ink">“{l.q}”</blockquote>
                <figcaption className="mt-6 font-sans text-xs uppercase tracking-[0.2em] text-muted-foreground">— {l.a}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <Faq faqs={HOME_FAQS} num="04 —" />
      <LatestJournal />
      <CtaBand />
    </>
  );
}
